import {
  type FieldPath,
  type FieldPathValue,
  type FieldValues,
} from 'react-hook-form'

import { getByPath } from './getByPath'

/**
 * オブジェクトから指定されたパスの値を返します。
 * オブジェクトやパスの値が null または undefined の場合はデフォルト値を返します。
 *
 * @template T - オブジェクトの型。
 * @template P - パスの型。
 * @param obj - オブジェクト。
 * @param path - パス。
 * @param defaultValue - デフォルト値。
 * @returns オブジェクトのパスの値。
 */
export function pathOr<T extends FieldValues, P extends FieldPath<T>>(
  obj: T | null | undefined,
  path: P,
  defaultValue: FieldPathValue<T, P>,
): FieldPathValue<T, P>

/**
 * オブジェクトから指定されたパスの値を返します。
 * オブジェクトやパスの値が null または undefined の場合はデフォルト値を返します。
 *
 * @template T - オブジェクトの型。
 * @template P - パスの型。
 * @param path - パス。
 * @param defaultValue - デフォルト値。
 * @returns オブジェクトを引数に取り、オブジェクトのパスの値を返す関数。
 */
export function pathOr<T extends FieldValues, P extends FieldPath<T>>(
  path: P,
  defaultValue: FieldPathValue<T, P>,
): (obj: T | null | undefined) => FieldPathValue<T, P>

/**
 * @param args - 引数。
 * @returns 結果または部分適用された関数。
 */
export function pathOr<T extends FieldValues, P extends FieldPath<T>>(
  ...args:
    [P, FieldPathValue<T, P>] | [T | null | undefined, P, FieldPathValue<T, P>]
):
  FieldPathValue<T, P> | ((obj: T | null | undefined) => FieldPathValue<T, P>) {
  const pathOrInner = (
    obj: T | null | undefined,
    path: P,
    defaultValue: FieldPathValue<T, P>,
  ): FieldPathValue<T, P> => {
    const value = getByPath(obj, path)

    return value ?? defaultValue
  }

  if (args.length === 2) {
    return (obj: T | null | undefined): FieldPathValue<T, P> =>
      pathOrInner(obj, ...args)
  }

  return pathOrInner(...args)
}
